import * as React from "react";
import { useUser } from "@/hooks/useUser";
import { addErrorSink, setUnloadFlush } from "@/utils/error-reporting";
import {
  sendErrorToBackend,
  resetTelemetryForUserChange,
  flushErrorQueue,
} from "@/services/telemetry.service";
import { initSentry, stopSentry, isSentryAllowedForUser } from "@/utils/sentry";

/**
 * Turn on crash reporting once we know who is signed in.
 *
 * Two destinations, deliberately:
 *  - Our own backend, always. It is the sovereign path and works for every
 *    customer, including firms in Australian mode.
 *  - Sentry, only for users we can positively confirm are NOT in Australian
 *    mode, and only in production.
 *
 * The order matters. Sentry stays off until the user is known and confirmed
 * allowed, and is stopped the instant the signed-in user changes — not when the
 * next user's details finally load. Otherwise one user's errors can leave under
 * the previous user's identity.
 */
export const useErrorReporting = (): void => {
  const { user } = useUser();
  const previousUserIdRef = React.useRef<string | null>(null);

  // Our own backend takes everything, from the moment the app starts.
  React.useEffect(() => {
    addErrorSink("backend", sendErrorToBackend);
    setUnloadFlush(flushErrorQueue);
  }, []);

  React.useEffect(() => {
    const currentId = user?.id ?? null;

    // The signed-in user changed (including signing out). Stop immediately and
    // drop anything queued under the previous session before doing anything
    // else — both are synchronous.
    if (previousUserIdRef.current !== currentId) {
      if (previousUserIdRef.current !== null) {
        stopSentry();
        resetTelemetryForUserChange();
      }
      previousUserIdRef.current = currentId;
    }

    if (user == null) return;

    if (!isSentryAllowedForUser(user)) {
      // Australian mode, or we cannot confirm otherwise. Make sure nothing is
      // running from earlier in this session.
      stopSentry();
      return;
    }

    void initSentry({ id: user.id, email: user.email, au_limiter: user.au_limiter });
  }, [user]);
};
